import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X } from "lucide-react";
import api from "@/api";
import { relativeTime } from "@/motion";
import { Progress, Reveal, SectionHead, mono } from "@/components/home/Primitives";

// Une seule incitation visible à la fois, priorité a > b > c, jamais réaffichée après fermeture.
export function Upsell({ id, text, t, onDismiss }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3 border border-dashed border-[var(--wl-line)] px-3 py-2" data-testid={`home-upsell-${id}`}>
      <Link to="/pricing" className={`${mono} text-[var(--wl-accent)] hover:underline`} data-testid={`home-upsell-${id}-link`}>{text}</Link>
      <button type="button" onClick={onDismiss} aria-label={t("common.cancel")} className="text-[var(--wl-muted)] hover:text-[var(--wl-text)]" data-testid={`home-upsell-${id}-close`}><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export function Collection({ state, collection, upsell, t }) {
  if (state === "visitor") {
    return (
      <Reveal as="section" testid="home-collection-visitor" className="border-t border-[var(--wl-line)] py-3">
        <p className="font-sg text-base font-bold uppercase text-[var(--wl-text)] md:text-lg">{t("home.visitor_title")}</p>
        <p className="mt-1 text-sm text-[var(--wl-muted)]">{t("home.visitor_body")}</p>
        <Link to="/auth" data-testid="home-visitor-signup" className="mt-3 inline-block border border-[var(--wl-accent)] px-3.5 py-1.5 wl-mono text-[10px] font-bold uppercase tracking-[.04em] text-[var(--wl-accent)] transition-colors hover:bg-[var(--wl-accent)] hover:text-[#0B0E14]">{t("home.visitor_cta")}</Link>
      </Reveal>
    );
  }
  if (!collection) return null;
  return (
    <Reveal as="section" testid="home-collection">
      <SectionHead title={t("home.collection_title")} link="/my-collection" linkLabel={t("home.see_all")} testid="home-collection-title" />
      {upsell}
      {collection.sets.length === 0 ? (
        <p className="text-sm text-[var(--wl-muted)]" data-testid="home-collection-empty">{t("home.collection_empty")} <Link to="/cards" className="text-[var(--wl-accent)] hover:underline">{t("nav.cards")}</Link></p>
      ) : collection.sets.map((s) => (
        <div key={s.set_id} className="border-b border-[var(--wl-line)] py-2" data-testid={`home-collection-set-${s.slug}`}>
          <div className="flex items-baseline justify-between gap-3">
            <Link to={`/cards/${s.slug}`} className="truncate font-sg text-[13px] font-bold uppercase text-[var(--wl-text)] hover:text-[var(--wl-accent)]">{s.set_name}</Link>
            <span className={mono}>{s.owned}/{s.total} · {s.pct}%</span>
          </div>
          <Progress pct={s.pct} label={t("home.missing_n", { n: Math.max(0, s.total - s.owned) })} testid={`home-collection-bar-${s.slug}`} />
        </div>
      ))}
      {state === "free" && !upsell && <p className={`${mono} mt-2`} data-testid="home-collection-free-hint">{t("home.free_sets_hint")}</p>}
    </Reveal>
  );
}

export function VisitDelta({ delta, upsell, t, lang }) {
  if (!delta || delta.players_moved === 0) return null;
  return (
    <Reveal as="section" testid="home-delta" className="border-l-2 border-l-[var(--wl-accent)] py-1.5 pl-3">
      <p className="text-sm text-[var(--wl-text)] sm:text-base">{t("home.delta_sentence", { n: delta.players_moved })} <span className={mono}>· {relativeTime(delta.since, lang)}</span></p>
      {upsell && <div className="mt-2">{upsell}</div>}
    </Reveal>
  );
}

export function Updated({ at, t, lang }) {
  if (!at) return null;
  return (
    <p className={`${mono} flex items-center gap-1.5`} data-testid="home-updated">
      <span className="rr-pulse inline-block h-1.5 w-1.5 rounded-full bg-[var(--wl-accent)]" />
      {t("home.updated", { when: relativeTime(at, lang) })}
    </p>
  );
}

// Nouveautés en session : vérification toutes les 3 min d'un endpoint qui ne renvoie qu'un entier.
// Aucun rafraîchissement automatique : l'utilisateur clique.
export function NewMilestones({ since, onRefresh, t }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!since) return undefined;
    const tick = () => api.get("/home/new-count", { params: { since } }).then((r) => setCount(r.data.count || 0)).catch(() => {});
    const id = setInterval(tick, 180000);
    return () => clearInterval(id);
  }, [since]);
  if (!count) return null;
  return (
    <button type="button" onClick={() => { setCount(0); onRefresh(); }} data-testid="home-new-milestones"
      className="fixed left-1/2 top-20 z-40 -translate-x-1/2 border border-[var(--wl-accent)] bg-[#0B0E14] px-4 py-2 wl-mono text-[10px] font-bold uppercase tracking-[.04em] text-[var(--wl-accent)] shadow-lg">
      <Bell className="mr-1.5 inline h-3.5 w-3.5" />{t("home.new_milestones", { n: count })}
    </button>
  );
}
